// ArchiveReportPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    FileSpreadsheet, Calendar, X, Download, Loader2, AlertCircle,
    CheckSquare, Square, ArrowLeft, Settings, Search
} from 'lucide-react';
import {
    API_URL, formatValue, formatAddress, formatAccumEndDate,
    renderCellValue, getArchiveTypeName, getDeviceType
} from './archiveUtils';
import { generateDeviceReports, getBuildAccumColumns, getGetAccumulatedValue } from './archiveHandlers';

// ==========================================
// Модальное окно настроек
// ==========================================
const ArchiveConfigModal = ({ isOpen, onClose, onGenerate, lastParams = null }) => {
    const [devices, setDevices] = useState([]);
    const [groups, setGroups] = useState([]);
    const [selectedDevices, setSelectedDevices] = useState([]);
    const [searchQuery, setSearchQuery] = useState(''); // ➕ Добавлено состояние поиска
    const [archiveType, setArchiveType] = useState('day');
    const [dateFrom, setDateFrom] = useState(() => {
        const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().split('T')[0];
    });
    const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            if (lastParams) {
                setArchiveType(lastParams.archiveType || 'day');
                setDateFrom(lastParams.dateFrom);
                setDateTo(lastParams.dateTo);
            } else {
                setArchiveType('day');
                const d = new Date(); d.setDate(d.getDate() - 7);
                setDateFrom(d.toISOString().split('T')[0]);
                setDateTo(new Date().toISOString().split('T')[0]);
            }
            loadData();
        }
    }, [isOpen]);

    const loadData = async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = { 'Authorization': `Bearer ${token}` };

            const [devicesRes, groupsRes] = await Promise.all([
                axios.get(`${API_URL}/api/devices`, { headers }),
                axios.get(`${API_URL}/api/devicegroups`, { headers })
            ]);

            const devicesList = Array.isArray(devicesRes.data) ? devicesRes.data : (devicesRes.data?.onServiceDevices || []);
            const sortedDevices = [...devicesList].sort((a, b) => formatAddress(a).localeCompare(formatAddress(b), 'ru-RU'));
            setDevices(sortedDevices);

            setGroups(groupsRes.data || []);

            setSelectedDevices(lastParams?.selectedDevices || []);
            setError(null);
        } catch (err) {
            setError(`Не удалось загрузить данные: ${err.message}`);
        }
    };

    // ➕ Фильтрация устройств по адресу или названию
    const filteredDevices = devices.filter(d => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        const addressStr = formatAddress(d).toLowerCase();
        const nameStr = (d.name || '').toLowerCase();
        return addressStr.includes(query) || nameStr.includes(query);
    });

    const handleGroupSelect = (groupId) => {
        if (!groupId) {
            setSelectedDevices([]);
            return;
        }
        const group = groups.find(g => g.id === parseInt(groupId));
        if (!group || !group.devices) return;

        const groupDeviceNames = new Set(group.devices);
        const newSelected = devices
            .filter(d => groupDeviceNames.has(d.name))
            .map(d => d.name);

        setSelectedDevices(newSelected);
    };

    const toggleSelectAll = () => {
        setSelectedDevices(selectedDevices.length === devices.length && devices.length > 0 ? [] : devices.map(d => d.name));
    };

    const toggleDevice = (name) => {
        setSelectedDevices(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
    };

    const handleSubmit = async () => {
        if (selectedDevices.length === 0) {
            setError('Выберите хотя бы одно устройство');
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            await onGenerate({ selectedDevices, archiveType, dateFrom, dateTo, devicesList: devices });
            onClose();
        } catch (err) {
            setError('Ошибка формирования отчета');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-slate-800 rounded-2xl border border-slate-700 w-full max-w-2xl flex flex-col shadow-2xl max-h-[90vh]">
                <div className="flex items-center justify-between p-4 border-b border-slate-700">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <Settings className="w-5 h-5 text-purple-400" /> Параметры отчета
                    </h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-700">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-6 space-y-5 custom-scrollbar">

                    <div>
                        <label className="block text-slate-400 text-xs mb-1.5">Быстрый выбор по группе</label>
                        <select
                            onChange={(e) => handleGroupSelect(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-purple-500"
                        >
                            <option value="">-- Выберите группу --</option>
                            {groups.map(g => (
                                <option key={g.id} value={g.id}>
                                    {g.name} ({g.devices?.length || 0} уст.)
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-slate-400 text-xs mb-1.5">Тепловычислители</label>

                        {/* ➕ Поле поиска */}
                        <div className="relative mb-2">
                            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Поиск по адресу или названию устройства..."
                                className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                        </div>

                        <div className="bg-slate-900 border border-slate-700 rounded-lg overflow-hidden">
                            <div className="sticky top-0 z-10 bg-slate-800 border-b border-slate-700 p-2 flex items-center justify-between">
                                <button onClick={toggleSelectAll} className="flex items-center gap-2 text-purple-400 hover:text-purple-300 transition text-left flex-1">
                                    {selectedDevices.length === devices.length && devices.length > 0 ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                                    <span className="text-white text-sm font-medium">Выбрать все ({devices.length})</span>
                                </button>
                                <span className="text-xs font-medium text-purple-300 bg-purple-500/10 px-2.5 py-1 rounded-full border border-purple-500/20 ml-2 whitespace-nowrap">
                                    Выбрано: {selectedDevices.length}
                                </span>
                            </div>
                            <div className="max-h-60 overflow-y-auto custom-scrollbar p-1">
                                {/* ➕ Обработка пустого результата поиска */}
                                {filteredDevices.length === 0 && (
                                    <div className="p-4 text-center text-slate-500 text-sm">
                                        {devices.length === 0 ? 'Нет доступных устройств' : 'Ничего не найдено'}
                                    </div>
                                )}
                                {/* ➕ Рендерим отфильтрованный список */}
                                {filteredDevices.map(d => {
                                    const devType = getDeviceType(d);
                                    const typeLabel = devType === 'aksioma03' ? 'Аксиома-03' : devType === 'aksioma01' ? 'Аксиома-01' : devType === 'karat307' ? 'Карат-307' : 'Неизвестный';
                                    const typeBadgeColor = devType === 'aksioma03' ? 'bg-blue-500/10 text-blue-300 border-blue-500/20' :
                                        devType === 'aksioma01' ? 'bg-green-500/10 text-green-300 border-green-500/20' :
                                            devType === 'karat307' ? 'bg-amber-500/10 text-amber-300 border-amber-500/20' :
                                                'bg-slate-500/10 text-slate-300 border-slate-500/20';
                                    return (
                                        <label key={d.name} className="flex items-start gap-3 p-2 hover:bg-slate-800 rounded cursor-pointer transition">
                                            <input type="checkbox" checked={selectedDevices.includes(d.name)} onChange={() => toggleDevice(d.name)}
                                                className="mt-1 w-4 h-4 rounded border-slate-500 text-purple-600 focus:ring-purple-500 bg-slate-700" />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-white text-sm font-medium truncate">{formatAddress(d)}</span>
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${typeBadgeColor} whitespace-nowrap`}>
                                                        {typeLabel}
                                                    </span>
                                                </div>
                                                <div className="text-slate-400 text-xs truncate">{d.name}</div>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-slate-400 text-xs mb-1.5">Тип архива</label>
                            <select value={archiveType} onChange={(e) => setArchiveType(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-purple-500">
                                <option value="hour">Часовой</option>
                                <option value="day">Суточный</option>
                                <option value="month">Месячный</option>
                                <option value="integral">Интегральный</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-slate-400 text-xs mb-1.5">С даты</label>
                            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-purple-500" />
                        </div>
                        <div>
                            <label className="block text-slate-400 text-xs mb-1.5">По дату</label>
                            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-purple-500" />
                        </div>
                    </div>
                    {error && (
                        <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                            <span>{error}</span>
                        </div>
                    )}
                </div>
                <div className="p-4 border-t border-slate-700 flex justify-end gap-3 bg-slate-800 rounded-b-2xl">
                    <button onClick={onClose} className="px-4 py-2 text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg text-sm font-medium transition">
                        Отмена
                    </button>
                    <button onClick={handleSubmit} disabled={isLoading || selectedDevices.length === 0}
                        className="px-6 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-medium transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
                        Сформировать
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==========================================
// Компонент отображения одного отчета
// ==========================================
const ReportCard = ({ report, lastParams, index, total }) => {
    const buildAccumCols = getBuildAccumColumns(report.deviceType);
    const getAccumVal = getGetAccumulatedValue(report.deviceType);
    const accumColumns = buildAccumCols(report.columns);

    const address = formatAddress(report.deviceFromList || report.deviceInfo);
    const deviceName = report.deviceInfo?.name || report.deviceName || 'Устройство';
    const serialNumber = report.deviceInfo?.serial_number || report.deviceInfo?.serialNumber || '—';
    const login = report.deviceInfo?.login ? `, логин: ${report.deviceInfo.login}` : '';
    const archiveTypeName = getArchiveTypeName(lastParams.archiveType);

    const deviceTypeLabel = report.deviceType === 'aksioma03' ? 'Аксиома-03' :
        report.deviceType === 'aksioma01' ? 'Аксиома-01' :
            report.deviceType === 'karat307' ? 'Карат-307' : 'Устройство';

    return (
        <div className="bg-white text-slate-900 rounded-xl p-6 sm:p-8 shadow-lg print:shadow-none print:p-0 print:rounded-none">
            <div className="mb-6 border-b-2 border-slate-800 pb-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    <p><span className="font-semibold">Адрес:</span> {address}</p>
                    <p><span className="font-semibold">Тепловычислитель:</span> {deviceName} ({deviceTypeLabel}), зав. номер: {serialNumber}{login}</p>
                    {report.podSystemName && (
                        <p><span className="font-semibold">Подсистема учета:</span> {report.podSystemName}</p>
                    )}
                    <p className="sm:col-span-2"><span className="font-semibold">Период:</span> с {new Date(lastParams.dateFrom).toLocaleDateString('ru-RU')} по {new Date(lastParams.dateTo).toLocaleDateString('ru-RU')} включительно ({archiveTypeName} архив)</p>
                </div>
            </div>

            {/* Основная таблица */}
            <div className="overflow-x-auto border border-slate-300 rounded-lg mb-6">
                <table className="w-full text-xs md:text-sm border-collapse">
                    <thead className="bg-slate-100 text-slate-700 font-semibold">
                        <tr>
                            {report.columns.map((col, cIdx) => (
                                <th key={cIdx} className="border border-slate-300 p-2 text-left">{col.name}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {report.records.map((r, rIdx) => (
                            <tr key={rIdx} className="hover:bg-slate-50">
                                {report.columns.map((col, cIdx) => (
                                    <td key={cIdx} className={`border border-slate-300 p-2 ${col.key === 'datetime' ? 'text-left whitespace-nowrap' : 'text-right'}`}>
                                        {renderCellValue(r[col.key], col)}
                                    </td>
                                ))}
                            </tr>
                        ))}

                        {/* Строка "Итого" — скрывается для Интегрального архива */}
                        {
                            report.archiveType !== 'integral' && (
                            <tr className="bg-slate-100 font-bold text-slate-800">
                                {report.columns.map((col, cIdx) => {
                                    if (col.key === 'datetime') return <td key={cIdx} className="border border-slate-300 p-2 text-left">Итого:</td>;
                                    const decimals = (col.unit?.includes('МПа') || col.unit?.includes('м³') || col.unit?.includes('т') || col.unit?.includes('Гкал')) ? 3 : (col.key.includes('time') ? 2 : 3);
                                    if (col.category === 'temperatura' || col.category === 'davlenie' || col.key.startsWith('temperatura_') || col.key.startsWith('davlenie_')) {
                                        return <td key={cIdx} className="border border-slate-300 p-2 text-right">{formatValue(report.averages[col.key], decimals)}</td>;
                                    }
                                    return <td key={cIdx} className="border border-slate-300 p-2 text-right">{formatValue(report.totals[col.key] || 0, decimals)}</td>;
                                })}
                            </tr>
                            )
                        }



                    </tbody>
                </table>
            </div>

            {/* Вторая таблица накопленных значений — скрывается для Интегрального архива */}
            {report.archiveType !== 'integral' && report.startAccumulated && report.endAccumulated && accumColumns.length > 0 && (
                <div className="overflow-x-auto border border-slate-300 rounded-lg">
                    <table className="w-full text-xs md:text-sm border-collapse">
                        <thead className="bg-slate-200 text-slate-800 font-bold">
                            <tr>
                                {accumColumns.map((col, cIdx) => (
                                    <th key={cIdx} className="border border-slate-300 p-2 text-left">{col.name}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                {accumColumns.map((col, cIdx) => {
                                    if (col.key === 'datetime') return <td key={cIdx} className="border border-slate-300 p-2 whitespace-nowrap">{new Date(lastParams.dateFrom).toLocaleDateString('ru-RU')} 00</td>;
                                    const val = getAccumVal(report.startAccumulated, col.key, report.podSystemIndex);
                                    const decimals = (col.unit?.includes('м³') || col.unit?.includes('т') || col.unit?.includes('Гкал')) ? 3 : 2;
                                    return <td key={cIdx} className="border border-slate-300 p-2 text-right">{formatValue(val, decimals)}</td>;
                                })}
                            </tr>
                            <tr>
                                {accumColumns.map((col, cIdx) => {
                                    if (col.key === 'datetime') return <td key={cIdx} className="border border-slate-300 p-2 whitespace-nowrap">{formatAccumEndDate(lastParams.dateTo)}</td>;
                                    const val = getAccumVal(report.endAccumulated, col.key, report.podSystemIndex);
                                    const decimals = (col.unit?.includes('м³') || col.unit?.includes('т') || col.unit?.includes('Гкал')) ? 3 : 2;
                                    return <td key={cIdx} className="border border-slate-300 p-2 text-right">{formatValue(val, decimals)}</td>;
                                })}
                            </tr>
                            <tr className="bg-slate-100 font-bold text-slate-800">
                                {accumColumns.map((col, cIdx) => {
                                    if (col.key === 'datetime') return <td key={cIdx} className="border border-slate-300 p-2 text-left">Итого:</td>;
                                    const startVal = getAccumVal(report.startAccumulated, col.key, report.podSystemIndex);
                                    const endVal = getAccumVal(report.endAccumulated, col.key, report.podSystemIndex);
                                    const getDiff = (end, start) => (parseFloat(end) || 0) - (parseFloat(start) || 0);
                                    const decimals = (col.unit?.includes('м³') || col.unit?.includes('т') || col.unit?.includes('Гкал')) ? 3 : 2;
                                    return <td key={cIdx} className="border border-slate-300 p-2 text-right">{formatValue(getDiff(endVal, startVal), decimals)}</td>;
                                })}
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}

            <div className="text-right text-xs text-slate-500 mt-4 print:mt-6">
                Страница {index + 1} из {total}
            </div>
        </div>
    );
};

// ==========================================
// Главная страница отчета
// ==========================================
const ArchiveReportPage = () => {
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(true);
    const [reportData, setReportData] = useState(null);
    const [lastParams, setLastParams] = useState(null);

    const handleGenerateReport = async (params) => {
        const token = localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}` };
        const allReports = [];

        let devicesList = params.devicesList || [];
        if (devicesList.length === 0) {
            try {
                const devRes = await axios.get(`${API_URL}/api/devices`, { headers });
                devicesList = Array.isArray(devRes.data) ? devRes.data : (devRes.data?.onServiceDevices || []);
            } catch (e) {
                console.warn('Не удалось загрузить список устройств для адресов');
            }
        }

        for (const devName of params.selectedDevices) {
            try {
                const deviceFromList = devicesList.find(d => d.name === devName) || null;
                const deviceType = getDeviceType(deviceFromList);

                if (deviceType === 'unknown') {
                    console.warn(`Пропущено устройство с неизвестным типом: ${devName}`);
                    continue;
                }

                const deviceReports = await generateDeviceReports(devName, deviceType, params, headers, deviceFromList);
                allReports.push(...deviceReports);
            } catch (err) {
                console.warn(`Ошибка при обработке ${devName}:`, err);
            }
        }

        if (allReports.length === 0) {
            throw new Error('Нет данных за выбранный период для выбранных устройств.');
        }

        setReportData(allReports);
        setLastParams(params);
    };

    const exportToExcel = () => {
        if (!reportData || !lastParams) return;

        let html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head><meta charset="utf-8"><style>
            @page { size: A4; margin: 1cm; }
            table { border-collapse: collapse; width: 100%; font-family: Arial, sans-serif; font-size: 10pt; }
            th, td { border: 1px solid #000; padding: 4px; text-align: center; }
            .header { text-align: left; font-weight: bold; border: none; padding: 2px; }
            .total-row { font-weight: bold; background-color: #f0f0f0; }
            .accum-header { background-color: #e6e6e6; font-weight: bold; }
            .page-break { page-break-before: always; }
            .page-num { text-align: right; border: none; padding-top: 10px; font-size: 9pt; color: #666; }
        </style></head><body>`;

        reportData.forEach((report, index) => {
            const buildAccumCols = getBuildAccumColumns(report.deviceType);
            const getAccumVal = getGetAccumulatedValue(report.deviceType);

            const address = formatAddress(report.deviceFromList || report.deviceInfo);
            const serialNumber = report.deviceInfo?.serial_number || report.deviceInfo?.serialNumber || '—';
            const deviceName = report.deviceInfo?.name || report.deviceName || 'Устройство';
            const login = report.deviceInfo?.login ? `, логин: ${report.deviceInfo.login}` : '';
            const archiveTypeName = getArchiveTypeName(lastParams.archiveType);
            const deviceTypeLabel = report.deviceType === 'aksioma03' ? 'Аксиома-03' :
                report.deviceType === 'aksioma01' ? 'Аксиома-01' :
                    report.deviceType === 'karat307' ? 'Карат-307' : 'Устройство';

            html += `<div class="${index > 0 ? 'page-break' : ''}">`;
            html += `<table>
            <tr><td colspan="${report.columns.length}" class="header">Адрес: ${address}</td></tr>
            <tr><td colspan="${report.columns.length}" class="header">Тепловычислитель: ${deviceName} (${deviceTypeLabel}), зав. номер: ${serialNumber}${login}</td></tr>`;

            if (report.podSystemName) {
                html += `<tr><td colspan="${report.columns.length}" class="header">Подсистема учета: ${report.podSystemName}</td></tr>`;
            }

            html += `<tr><td colspan="${report.columns.length}" class="header">Период: ${new Date(lastParams.dateFrom).toLocaleDateString('ru-RU')} - ${new Date(lastParams.dateTo).toLocaleDateString('ru-RU')} включительно (${archiveTypeName} архив)</td></tr>
            <tr><td colspan="${report.columns.length}" style="border:none; height: 10px;"></td></tr>
            <tr>`;

            report.columns.forEach(col => {
                html += `<th>${col.name}</th>`;
            });
            html += `</tr>`;

            // Внутри exportToExcel — замена блока отрисовки ячеек записи
            report.records.forEach(r => {
                html += `<tr>`;
                report.columns.forEach(col => {
                    if (col.type === 'datetime') {
                        html += `<td style="text-align:left;">${r[col.key] || '—'}</td>`;
                    } else if (col.type === 'ns') {
                        const nsVal = r[col.key];
                        html += `<td style="text-align:center;">${nsVal > 0 ? `⚠(${nsVal})` : '✓'}</td>`;
                    } else {
                        const decimals = (col.unit?.includes('МПа') || col.unit?.includes('м³') || col.unit?.includes('т') || col.unit?.includes('Гкал')) ? 3 : (col.type === 'time' ? 3 : 3);
                        html += `<td style="text-align:right;">${formatValue(r[col.key], decimals)}</td>`;
                    }
                });
                html += `</tr>`;
            });

            if (report.archiveType !== 'integral') {
                html += `<tr class="total-row"><td style="text-align:left;">Итого:</td>`;
                report.columns.forEach(col => {
                    if (col.key === 'datetime') return;
                    const decimals = (col.unit?.includes('МПа') || col.unit?.includes('м³') || col.unit?.includes('т') || col.unit?.includes('Гкал')) ? 3 : (col.key.includes('time') ? 2 : 3);

                    // ✅ ИСПРАВЛЕНО: проверка по category (работает для всех 3 типов устройств)
                    if (col.category === 'temperatura' || col.category === 'davlenie' || col.key.startsWith('temperatura_') || col.key.startsWith('davlenie_')) {
                        html += `<td style="text-align:right;">${formatValue(report.averages[col.key], decimals)}</td>`;
                    } else {
                        html += `<td style="text-align:right;">${formatValue(report.totals[col.key] || 0, decimals)}</td>`;
                    }
                });
                html += `</tr>`;
            }

            if (report.archiveType !== 'integral' && report.startAccumulated && report.endAccumulated) {
                const getDiff = (endVal, startVal) => (parseFloat(endVal) || 0) - (parseFloat(startVal) || 0);
                const accumColumns = buildAccumCols(report.columns);

                html += `<tr><td colspan="${report.columns.length}" style="border:none; height: 15px;"></td></tr>`;
                html += `<tr>`;
                accumColumns.forEach(col => {
                    html += `<th class="accum-header">${col.name}</th>`;
                });
                html += `</tr>`;

                html += `<tr><td style="text-align:left;">${new Date(lastParams.dateFrom).toLocaleDateString('ru-RU')} 00</td>`;
                accumColumns.forEach(col => {
                    if (col.key === 'datetime') return;
                    const val = getAccumVal(report.startAccumulated, col.key, report.podSystemIndex);
                    const decimals = (col.unit?.includes('м³') || col.unit?.includes('т') || col.unit?.includes('Гкал')) ? 3 : 2;
                    html += `<td style="text-align:right;">${formatValue(val, decimals)}</td>`;
                });
                html += `</tr>`;

                html += `<tr><td style="text-align:left;">${formatAccumEndDate(lastParams.dateTo)}</td>`;
                accumColumns.forEach(col => {
                    if (col.key === 'datetime') return;
                    const val = getAccumVal(report.endAccumulated, col.key, report.podSystemIndex);
                    const decimals = (col.unit?.includes('м³') || col.unit?.includes('т') || col.unit?.includes('Гкал')) ? 3 : 2;
                    html += `<td style="text-align:right;">${formatValue(val, decimals)}</td>`;
                });
                html += `</tr>`;

                html += `<tr class="total-row"><td style="text-align:left;">Итого:</td>`;
                accumColumns.forEach(col => {
                    if (col.key === 'datetime') return;
                    const startVal = getAccumVal(report.startAccumulated, col.key, report.podSystemIndex);
                    const endVal = getAccumVal(report.endAccumulated, col.key, report.podSystemIndex);
                    const decimals = (col.unit?.includes('м³') || col.unit?.includes('т') || col.unit?.includes('Гкал')) ? 3 : 2;
                    html += `<td style="text-align:right;">${formatValue(getDiff(endVal, startVal), decimals)}</td>`;
                });
                html += `</tr>`;
            }

            html += `<tr><td colspan="${report.columns.length}" class="page-num">Страница ${index + 1} из ${reportData.length}</td></tr>`;
            html += `</table></div>`;
        });

        html += `</body></html>`;

        const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Отчет_архив_${lastParams.dateFrom}_${lastParams.dateTo}.xls`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="p-4 sm:p-6 space-y-4 max-w-7xl mx-auto min-h-screen">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <button onClick={() => navigate('/reports')} className="flex items-center gap-2 text-slate-400 hover:text-white transition text-sm font-medium">
                    <ArrowLeft className="w-4 h-4" /> Назад к отчетным формам
                </button>

                {reportData && (
                    <div className="flex gap-2">
                        <button onClick={() => setIsModalOpen(true)} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium transition flex items-center gap-2">
                            <Settings className="w-4 h-4" /> Изменить параметры
                        </button>
                        <button onClick={exportToExcel} className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium transition flex items-center gap-2">
                            <Download className="w-4 h-4" /> Скачать Excel
                        </button>
                    </div>
                )}
            </div>

            {!reportData ? (
                <div className="flex flex-col items-center justify-center py-20 bg-slate-800/50 rounded-2xl border border-slate-700 border-dashed">
                    <FileSpreadsheet className="w-16 h-16 text-slate-600 mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">Отчет не сформирован</h3>
                    <p className="text-slate-400 text-sm mb-6 text-center max-w-md">
                        Нажмите кнопку ниже, чтобы настроить параметры (устройства, период, тип архива) и сгенерировать отчет.
                    </p>
                    <button onClick={() => setIsModalOpen(true)} className="px-6 py-3 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-sm font-medium transition flex items-center gap-2 shadow-lg shadow-purple-900/20">
                        <Settings className="w-5 h-5" /> Настроить и сформировать отчет
                    </button>
                </div>
            ) : (
                <div className="space-y-8">
                    {reportData.map((report, idx) => (
                        <ReportCard
                            key={idx}
                            report={report}
                            lastParams={lastParams}
                            index={idx}
                            total={reportData.length}
                        />
                    ))}
                </div>
            )}

            <ArchiveConfigModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onGenerate={handleGenerateReport}
                lastParams={lastParams}
            />
        </div>
    );
};

export default ArchiveReportPage;